import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { ILimit } from 'generated/sanity-graphql';
import { useLoyaltyEngineRewards } from 'hooks/use-loyalty-engine-rewards';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IncentiveEvaluationErrorCodes, IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import {
  getWalmartOfferUnavailableMessage,
  getWalmartSeasonalConfigOfferIds,
  isWalmartOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import {
  AppOffer,
  AppOfferListItem,
  AppPersonalizedOffer,
  EngineOfferListItem,
  SanityOfferListItem,
  appOfferToLoyaltyOffer,
} from 'state/loyalty/types';
import { useServiceModeContext } from 'state/service-mode';

import { useLoyaltyIncentivesAvailabilityUtils } from '../loyalty-incentives-components/use-loyalty-incentives-availability';
import { useLoyaltyWalmart } from '../loyalty-partners/walmart/use-loyalty-walmart';

import { useLoyaltyOffersFilters } from './use-loyalty-offers-filters';
import { incentiveHasEvaluationError, reduceOffers } from './utils';

export interface UseReduceOffersProps {
  sanityOfferListItems: SanityOfferListItem[];
  appPersonalizedOffers: AppPersonalizedOffer[];
  engineOfferListItems: EngineOfferListItem[];
  offersLoading: boolean;
  refreshOffersLoading?: boolean;
}

export function useReduceOffers({
  sanityOfferListItems,
  appPersonalizedOffers,
  engineOfferListItems,
  offersLoading,
  refreshOffersLoading = false,
}: UseReduceOffersProps) {
  const formatters = useIntl();

  const { data: loyaltyWalmart } = useLoyaltyWalmart();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const {
    checkLoyaltyOfferAvailability,
    getIncentiveUnavailableMessage,
  } = useLoyaltyIncentivesAvailabilityUtils();

  const walmartConfigOffers = loyaltyWalmart?.seasonalPromoConfig?.configOffers;
  const { engineRewardsMap } = useLoyaltyEngineRewards();

  const enableSortOffersForServiceMode = useFlag(
    LaunchDarklyFlag.ENABLE_SORT_OFFERS_FOR_SERVICE_MODE
  );
  const { appliedFilters } = useLoyaltyOffersFilters();
  const dispatch = useAppDispatch();

  const [offerListItems, setOfferListItems] = useState([] as AppOfferListItem[]);
  const [offersInCart, setOffersInCart] = useState([] as AppOffer[]);

  const [reductionLoading, setReductionLoading] = useState<boolean>(true);

  const { serviceMode } = useServiceModeContext();

  const enableOfferGroups = useFlag(LaunchDarklyFlag.ENABLE_OFFER_GROUPS_V2);

  const runOffersReduction = useCallback(() => {
    if (!sanityOfferListItems?.length || offersLoading || refreshOffersLoading) {
      return;
    }

    setReductionLoading(true);

    try {
      const [_offerItems, _offersInCart] = reduceOffers({
        sanityOfferListItems,
        appPersonalizedOffers,
        engineOfferListItems,
        appliedOffers,
        inRestaurantRedemptionCart,
        checkLoyaltyOfferAvailability,
        getIncentiveUnavailableMessage: (incentive: AppOffer) => {
          const loyaltyEngineId = incentive.loyaltyEngineId;
          if (!loyaltyEngineId) {
            return;
          }

          // Override Walmart seasonal promo max redemption availability error with custom message
          if (
            getWalmartSeasonalConfigOfferIds(loyaltyWalmart).includes(incentive.cmsId) &&
            walmartConfigOffers &&
            incentiveHasEvaluationError(
              incentive,
              IncentiveEvaluationErrorCodes.EXCEEDS_MAX_REDEMPTION
            )
          ) {
            const isDayLimit =
              (walmartConfigOffers
                ?.find(configOffer => configOffer?._id === incentive.cmsId)
                ?.rules?.find(rule => rule?.__typename === 'Limit') as ILimit)?.interval === 'day';

            return isDayLimit
              ? formatters.formatMessage({
                  id: 'availableAgainTomorrow',
                  defaultMessage: 'Available Again Tomorrow',
                })
              : formatters.formatMessage({ id: 'offerNotAvailable' });
          }

          // Override Walmart offer max redemption availability error with custom message
          if (isWalmartOffer(incentive)) {
            if (
              incentiveHasEvaluationError(
                incentive,
                IncentiveEvaluationErrorCodes.EXCEEDS_MAX_REDEMPTION
              )
            ) {
              return getWalmartOfferUnavailableMessage(incentive.cmsId, formatters);
            }
          }

          return getIncentiveUnavailableMessage({
            incentive: appOfferToLoyaltyOffer(incentive),
            pointCost: engineRewardsMap[loyaltyEngineId]?.pointCost,
            locked: engineRewardsMap[loyaltyEngineId]?.locked,
          });
        },
        enableSortOffersForServiceMode,
        setOffersFeedbackMap: (offerFeedbackMap: IncentiveEvaluationMap) =>
          dispatch(actions.loyalty.setOffersFeedbackMap(offerFeedbackMap)),
        appliedFilters,
        selectedServiceMode: serviceMode,
        formatters,
        enableOfferGroups,
      });

      setOfferListItems(_offerItems);
      setOffersInCart(_offersInCart);
    } catch (e) {
      // If the offers reduce fails, we ensure the user will not see and endless loading screen,
      // and instead sees the offers empty screens
      setReductionLoading(false);
    } finally {
      setReductionLoading(false);
    }
  }, [
    sanityOfferListItems,
    offersLoading,
    refreshOffersLoading,
    appPersonalizedOffers,
    engineOfferListItems,
    appliedOffers,
    inRestaurantRedemptionCart,
    checkLoyaltyOfferAvailability,
    enableSortOffersForServiceMode,
    appliedFilters,
    serviceMode,
    formatters,
    setOfferListItems,
    setOffersInCart,
    loyaltyWalmart,
    walmartConfigOffers,
    getIncentiveUnavailableMessage,
    engineRewardsMap,
    dispatch,
    enableOfferGroups,
  ]);

  // @TODO: We should review this, how we trigger the offers reduction
  useEffect(() => {
    runOffersReduction();
  }, [runOffersReduction]);

  return {
    offerListItems,
    offersInCart,
    reductionLoading,
    reprocessOffers: runOffersReduction,
  };
}
